<template>
  <div class="carousel-container">
    <div class="splide" role="group">
      <div class="splide__track">
        <ul class="splide__list">
          <li v-for="petition in petitions" :key="petition.slug" class="splide__slide">
            <a :href="petition.url" class="petition">
              <div class="splide__slide__container">
                <div v-if="petition.successful" class="successful-petition-wrapper">
                  <i class="icon-ok-circled" />
                </div>
                <div class="image-container">
                  <img v-if="petition.imageUrl" :src="petition.imageUrl" class="petition-image mx-auto d-block">
                  <div v-else class="petition-image" />
                </div>
                <div class="row">
                  <div class="col-7">
                    <progress-bar :percentage-done="petitionProgress(petition)" class="petition-progress-bar" />
                  </div>
                  <div class="col-5">
                    <span class="petition-progress-label">{{ petitionProgressLabel(petition) }}</span>
                  </div>
                </div>
              </div>
              <span class="title d-block fw-bold">{{ petition.title }}</span>
              <p class="why">{{ petition.why }}</p>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import '@splidejs/splide/css'

import ajax from '@/helpers/ajax'
import { nextTick } from 'vue'
import ProgressBar from '@/components/progress-bar'
import Splide from '@splidejs/splide'

const PETITIONS_CAROUSEL_PAGE_SIZE = 3

export default {
  components: { ProgressBar },

  props: {
    petitionsListPath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      fetchingPetitions: false,
      lastPageFetched: 0,
      petitions: [],
      splide: null,
      totalPetitionPages: null
    }
  },

  mounted() {
    this.fetchPetitions().then(() => {
      nextTick(() => {
        // Wait until the list is rendered before initializing the carousel
        this.setupCarousel()
      })
    })
  },

  methods: {
    fetchPetitions() {
      // Avoid race conditions by only allowing one fetch at a time
      if (this.fetchingPetitions) {
        return Promise.resolve()
      }

      this.fetchingPetitions = true

      return ajax.get({
        url: this.petitionsListPath,
        params: {
          page: this.lastPageFetched + 1
        },
        successHandler: (resp) => {
          this.petitions = this.petitions.concat(resp.results)
          this.lastPageFetched = resp.currentPage
          this.totalPetitionPages = resp.totalPages
        }
      }).then(() => {
        this.fetchingPetitions = false
      })
    },

    petitionProgress(petition) {
      return Math.floor((petition.signatureCount * 100) / petition.goal)
    },

    petitionProgressLabel(petition) {
      return this.i18n.t('public.petitions.progress_bar.signatures_count_label', {count: petition.signatureCount})
    },

    setupCarousel() {
      const direction = this.i18n.isRtl() ? 'rtl' : 'ltr'
      const options = {
        breakpoints: {
          768: {
            perPage: 1,
            gap: '1rem'
          },
          992: {
            perPage: 2,
            gap: '1rem'
          }
        },
        classes: {
          arrows: 'splide__arrows arrows',
          arrow : 'splide__arrow arrow',
          prev  : 'splide__arrow--prev arrow-prev',
          next  : 'splide__arrow--next arrow-next'
        },
        direction: direction,
        gap: '2rem',
        i18n: {
          carousel: this.i18n.t('public.petitions.carousel.description'),
          next: this.i18n.t('public.petitions.carousel.next'),
          prev: this.i18n.t('public.petitions.carousel.previous'),
          slideLabel: this.i18n.t('public.petitions.carousel.slide_index')
        },
        pagination: false,  // Don't show page dots
        paginationDirection: direction,
        perPage: PETITIONS_CAROUSEL_PAGE_SIZE,
        perMove: 1,
        rewind: false // Don't allow the carousel to loop
      }

      this.splide = new Splide('.splide', options).mount()

      // Lazy load petitions as user moves along the carousel
      // The newIndex argument is an integer with the value of the left-most slide currently being displayed
      // (see https://splidejs.com/guides/events/#moved for details)
      this.splide.on('moved', (newIndex) => {
        // Calculate what's the next page for the one we're currently displaying elements
        const nextPage = Math.floor((newIndex + 2) / PETITIONS_CAROUSEL_PAGE_SIZE) + 2
        // Now calculate how many elements we should have fetched to be able to display the next page entirely
        const petitionsCountReadyForDisplay = PETITIONS_CAROUSEL_PAGE_SIZE * nextPage

        // If there are additional petitions to be fetched, and we haven't retrieved the petitions for the next page yet,
        // fetch the petitions
        if (this.totalPetitionPages > this.lastPageFetched && petitionsCountReadyForDisplay >= this.petitions.length) {
          this.fetchPetitions().then(() => {
            nextTick(() => {
              // Wait until the new elements are added to the list before refreshing the carousel
              this.splide.refresh()
            })
          })
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
@use "~common_styles/breakpoints" as breakpoints;

// Set a fixed height to avoid automated scrolling when re-rendering the carousel on refresh
.carousel-container {
  height: 450px;

  ::v-deep(.splide__arrows) {
    .arrow-prev {
      left: -50px !important;
    }

    .arrow-next {
      right: -50px !important;
    }
  }

  ::v-deep(.arrow) {
    height: 55px;
    width: 30px;
    background: #000000;
    border-radius: 0;
    opacity: 1;

    &:disabled {
      opacity: 20%;
    }

    svg {
      fill: white;
    }
  }

  @include breakpoints.screen-mobile-only {
    // On mobiles, where the arrow butons are displayed on top the items,
    // align them with the image's bottom
    ::v-deep(.arrow) {
      top: 125px;
    }

    ::v-deep(.splide__arrows) {
      .arrow-prev {
        left: 0 !important;
      }
      .arrow-next {
        right: 0 !important;
      }
    }
  }
}

.successful-petition-wrapper {
  position: absolute;
  right: 0;
  font-size: 2em;

  i {
    display: block;
    margin-top: -6px;
    margin-right: -4px;
  }
}

a.petition {
  &:hover {
    text-decoration: none;
  }
}
.image-container {
  background: #f4f5f7;
}
.petition-image {
  height: 160px;
  width: auto;
  max-width: 100%;
  object-fit: cover;
}
.title {
  margin: 10px 0;
  color: #333;
  font-size: 19px;
}

.why {
  color: #333;
  font-size: 14px;
}

.petition-progress-bar {
  margin: 0;
  border-radius: 0;
  height: 20px;
}

.petition-progress-label {
  display: block;
  width: 100%;
  font-size: 12px;
  text-align: right;
  margin: auto 0;
  margin-right: 15px;
  color: #333;
}
</style>
